/**
 * Sevv design tokens aligned with Sevv-Ui-Theme-Guidelines (vault).
 * Persisted as JSON on WorkspaceSettings.theme_tokens (Back4App).
 */

export const SEVV_THEME_TOKENS_VERSION = 5 as const

/**
 * Optional overrides for in-room game HUD / menus / panels.
 * Unset keys resolve from palette in applySevvCssVariables.
 */
export interface SevvGameInRoomSurfaces {
  toolbarGradientStart?: string
  toolbarGradientEnd?: string
  toolbarBorder?: string
  iconButtonBg?: string
  iconButtonHoverBg?: string
  menuPaperBg?: string
  panelPaperBg?: string
  accentMuted?: string
  /** Optional; defaults to primary→primaryLight gradient (same as global dialog title). */
  dialogTitleBackground?: string
  dialogTitleColor?: string
  dialogTitleBorderBottom?: string
}

export interface SevvThemeSurfaces {
  gameInRoom?: Partial<SevvGameInRoomSurfaces>
}

/** Shell / modal chrome persisted with theme_tokens (Environment Appearance). */
export interface SevvThemeChrome {
  /** MUI Modal backdrop behind dialogs (e.g. rgba(0,0,0,0.5)). */
  overlayBackdrop: string
  /**
   * Backdrop behind a video tile while its camera picture is missing or still
   * loading (waiting placeholder, PiP tiles). Read as --sevv-video-tile-bg.
   */
  videoTileBackground: string
  /** Main menu sidebar; empty uses palette.backgroundPaper. */
  shellSidebarBackground?: string
  /** App header / AppBar; empty uses palette.primary. */
  shellHeaderBackground?: string
  /**
   * MuiDialogTitle root `background` (color or full CSS e.g. linear-gradient).
   * Empty uses default primary → primaryLight gradient.
   */
  dialogTitleBackground?: string
  /** Title text/icon color; empty defaults to #ffffff on the default gradient. */
  dialogTitleColor?: string
  /** `border-bottom` for title bar; empty defaults to 1px primary tint. */
  dialogTitleBorderBottom?: string
  /** MuiDialogActions footer `background`; empty uses primaryPale at ~4% mix feel via rgba tint. */
  dialogActionsBackground?: string
  /** `border-top` for dialog actions row; empty uses toolbar-style primary tint. */
  dialogActionsBorderTop?: string
  /**
   * Translucent chip/panel surface for content floated over arbitrary backgrounds
   * (e.g. reaction author labels over screen-share video, glass dialogs).
   * Empty defaults to a tint of the resolved `backgroundPaper` for the active mode,
   * so it stays legible against `--sevv-text-primary` in both light and dark.
   */
  surfaceOverlay?: string
  /**
   * Low-emphasis border/divider for content floated over a `surfaceOverlay` glass surface
   * (e.g. dialog/title/actions edges on a translucent panel). Empty defaults to a low-opacity
   * tint of the resolved `textPrimary`, so it stays faint-but-visible in both light and dark.
   */
  borderSubtle?: string
}

export interface SevvThemeTokens {
  version: typeof SEVV_THEME_TOKENS_VERSION
  palette: {
    primary: string
    primaryLight: string
    primaryDark: string
    primaryPale: string
    secondary: string
    success: string
    warning: string
    error: string
    info: string
    backgroundDefault: string
    backgroundPaper: string
    textPrimary: string
    textSecondary: string
    divider: string
  }
  /** Optional dark-mode palette overrides (merged when mode is dark). */
  darkPalette?: Partial<SevvThemeTokens['palette']>
  /** Modal backdrop, shell sidebar/header (v2+). */
  chrome?: SevvThemeChrome
  /** Optional dark-mode overrides for chrome (merged when mode is dark; v3+). */
  chromeDark?: Partial<SevvThemeChrome>
  /** Optional per-area surface overrides (v4+); game cluster uses CSS vars --sevv-surface-game-* (incl. dialog title v5). */
  surfaces?: SevvThemeSurfaces
}

export interface BrandingExtension {
  faviconUrl?: string
  platformDescription?: string
  logoAltText?: string
  accentColor?: string
  textColor?: string
  backgroundColor?: string
  authDialogPlatformName?: string
  authDialogLogoUrl?: string
}

const DEFAULT_CHROME: SevvThemeChrome = {
  overlayBackdrop: 'rgba(0, 0, 0, 0.5)',
  // The colour the tiles hard-coded before this became a token: nothing renders differently.
  videoTileBackground: 'rgba(0, 0, 0, 0.8)',
}

function hexToRgbTriplet(hex: string): string {
  const h = hex.replace('#', '')
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16)
    const g = parseInt(h[1] + h[1], 16)
    const b = parseInt(h[2] + h[2], 16)
    return `${r}, ${g}, ${b}`
  }
  if (h.length !== 6) return '249, 116, 21'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

/** Fully resolved chrome for MUI + CSS vars (no undefined optional slots). */
export type ResolvedSevvChrome = SevvThemeChrome & {
  shellSidebarBackground: string
  shellHeaderBackground: string
  dialogTitleBackground: string
  dialogTitleColor: string
  dialogTitleBorderBottom: string
  dialogActionsBackground: string
  dialogActionsBorderTop: string
  surfaceOverlay: string
  borderSubtle: string
}

export const DEFAULT_SEVV_THEME_TOKENS: SevvThemeTokens = {
  version: SEVV_THEME_TOKENS_VERSION,
  palette: {
    primary: '#f97415',
    primaryLight: '#ffa047',
    primaryDark: '#d65c05',
    primaryPale: '#fff2d6',
    secondary: '#3498db',
    success: '#27ae60',
    warning: '#f39c12',
    error: '#e74c3c',
    info: '#3498db',
    backgroundDefault: '#fafafa',
    backgroundPaper: '#ffffff',
    textPrimary: '#424242',
    textSecondary: '#757575',
    divider: '#eeeeee',
  },
  darkPalette: {
    backgroundDefault: '#121212',
    backgroundPaper: '#1e1e1e',
    textPrimary: '#f5f5f5',
    textSecondary: '#b0b0b0',
    divider: '#333333',
  },
  chrome: { ...DEFAULT_CHROME },
  surfaces: {},
}

/** Merged chrome before palette fallbacks (light base + optional dark layer). */
function mergedChromeInput(tokens: SevvThemeTokens, mode: 'light' | 'dark'): Partial<SevvThemeChrome> {
  const base: Partial<SevvThemeChrome> = { ...DEFAULT_CHROME, ...tokens.chrome }
  if (mode !== 'dark' || !tokens.chromeDark) return base
  const d = tokens.chromeDark
  if (d.overlayBackdrop !== undefined && d.overlayBackdrop.trim() !== '') {
    base.overlayBackdrop = d.overlayBackdrop.trim()
  }
  if (d.videoTileBackground !== undefined && d.videoTileBackground.trim() !== '') {
    base.videoTileBackground = d.videoTileBackground.trim()
  }
  if (d.shellSidebarBackground !== undefined) {
    const t = d.shellSidebarBackground.trim()
    if (t) base.shellSidebarBackground = t
    else delete base.shellSidebarBackground
  }
  if (d.shellHeaderBackground !== undefined) {
    const t = d.shellHeaderBackground.trim()
    if (t) base.shellHeaderBackground = t
    else delete base.shellHeaderBackground
  }
  const mergeDarkOptional = (key: keyof SevvThemeChrome) => {
    const v = d[key]
    if (v === undefined) return
    const t = typeof v === 'string' ? v.trim() : ''
    if (t) (base as Record<string, string>)[key as string] = t
    else delete (base as Record<string, unknown>)[key as string]
  }
  mergeDarkOptional('dialogTitleBackground')
  mergeDarkOptional('dialogTitleColor')
  mergeDarkOptional('dialogTitleBorderBottom')
  mergeDarkOptional('dialogActionsBackground')
  mergeDarkOptional('dialogActionsBorderTop')
  mergeDarkOptional('surfaceOverlay')
  mergeDarkOptional('borderSubtle')
  return base
}

/** Resolved shell colors for MUI / CSS (defaults follow resolved palette for the active mode). */
export function resolveEffectiveChrome(
  tokens: SevvThemeTokens,
  resolvedPalette: SevvThemeTokens['palette'],
  mode: 'light' | 'dark'
): ResolvedSevvChrome {
  const ch = mergedChromeInput(tokens, mode)
  const p = resolvedPalette
  const rgb = hexToRgbTriplet(p.primary)
  const defaultTitleBg = `linear-gradient(135deg, ${p.primary} 0%, ${p.primaryLight} 100%)`
  const defaultTitleColor = '#ffffff'
  const defaultTitleBorder = `1px solid rgba(${rgb}, 0.3)`
  const defaultActionsBg = `rgba(${rgb}, 0.04)`
  const defaultActionsBorder = `1px solid rgba(${rgb}, 0.2)`
  /** Frosted tint of the resolved paper color, so it reads correctly against textPrimary in both modes. */
  const defaultSurfaceOverlay = `rgba(${hexToRgbTriplet(p.backgroundPaper)}, 0.72)`
  /** Faint tint of the resolved text color, so glass-surface edges stay subtle in both modes. */
  const defaultBorderSubtle = `rgba(${hexToRgbTriplet(p.textPrimary)}, 0.1)`
  return {
    overlayBackdrop: ch.overlayBackdrop?.trim() || DEFAULT_CHROME.overlayBackdrop,
    videoTileBackground: ch.videoTileBackground?.trim() || DEFAULT_CHROME.videoTileBackground,
    shellSidebarBackground: (ch.shellSidebarBackground?.trim() || p.backgroundPaper).trim(),
    shellHeaderBackground: (ch.shellHeaderBackground?.trim() || p.primary).trim(),
    dialogTitleBackground: ch.dialogTitleBackground?.trim() || defaultTitleBg,
    dialogTitleColor: ch.dialogTitleColor?.trim() || defaultTitleColor,
    dialogTitleBorderBottom: ch.dialogTitleBorderBottom?.trim() || defaultTitleBorder,
    dialogActionsBackground: ch.dialogActionsBackground?.trim() || defaultActionsBg,
    dialogActionsBorderTop: ch.dialogActionsBorderTop?.trim() || defaultActionsBorder,
    surfaceOverlay: ch.surfaceOverlay?.trim() || defaultSurfaceOverlay,
    borderSubtle: ch.borderSubtle?.trim() || defaultBorderSubtle,
  }
}

/** Merged palette for the active appearance mode (dark overlays `darkPalette` on `palette`). */
export function resolveEffectivePalette(tokens: SevvThemeTokens, mode: 'light' | 'dark'): SevvThemeTokens['palette'] {
  if (mode === 'dark' && tokens.darkPalette) {
    return { ...tokens.palette, ...tokens.darkPalette }
  }
  return tokens.palette
}

/**
 * Normalizes workspace JSON fields: Parse / HTTP may return either a JSON string or an already-parsed object.
 */
export function parseJsonField<T>(raw: unknown, fallback: T): T {
  if (raw == null || raw === '') return fallback
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    return raw as T
  }
  if (typeof raw === 'string') {
    try {
      const v = JSON.parse(raw) as T
      return v && typeof v === 'object' ? v : fallback
    } catch {
      return fallback
    }
  }
  return fallback
}

export function stringifyJsonField(obj: unknown): string {
  return JSON.stringify(obj)
}

export function mergeThemeTokens(
  base: SevvThemeTokens,
  partial: Partial<SevvThemeTokens> | null | undefined
): SevvThemeTokens {
  if (!partial) return base
  const baseChrome = { ...DEFAULT_CHROME, ...base.chrome }
  const partialChrome: Partial<SevvThemeChrome> = partial.chrome ?? {}
  const baseChromeDark = { ...(base.chromeDark ?? {}) }
  const partialChromeDark: Partial<SevvThemeChrome> = partial.chromeDark ?? {}
  const baseSurfaces = base.surfaces ?? {}
  const partialSurfaces = partial.surfaces ?? {}
  const mergedGameInRoom = {
    ...(baseSurfaces.gameInRoom ?? {}),
    ...(partialSurfaces.gameInRoom ?? {}),
  }
  return {
    version: SEVV_THEME_TOKENS_VERSION,
    palette: { ...base.palette, ...partial.palette },
    darkPalette: { ...base.darkPalette, ...partial.darkPalette },
    chrome: {
      ...baseChrome,
      ...partialChrome,
      overlayBackdrop: partialChrome.overlayBackdrop ?? baseChrome.overlayBackdrop,
      videoTileBackground: partialChrome.videoTileBackground ?? baseChrome.videoTileBackground,
    },
    chromeDark: { ...baseChromeDark, ...partialChromeDark },
    surfaces: {
      gameInRoom: mergedGameInRoom,
    },
  }
}

export function themeTokensFromLegacyUrls(urls: Record<string, string> | undefined): Partial<SevvThemeTokens> {
  if (!urls) return {}
  const palette: Partial<SevvThemeTokens['palette']> = {}
  if (urls.primaryColor) palette.primary = urls.primaryColor
  if (urls.secondaryColor) palette.secondary = urls.secondaryColor
  if (urls.textColor) {
    palette.textPrimary = urls.textColor
  }
  // Intentionally do not map urls.backgroundColor into palette.backgroundDefault / backgroundPaper.
  // That legacy field was the old dark app chrome (#1e2232); global app background now comes from
  // theme_tokens (Appearance) or DEFAULT_SEVV_THEME_TOKENS. Branding "background color" stays on
  // WorkspaceSettings.urls / branding_extension for surfaces that opt into it explicitly.
  return Object.keys(palette).length ? { palette: palette as SevvThemeTokens['palette'] } : {}
}
